// src/modules/units/tyres/views/action/tyreassociationform.view.js
//
// Pure markup for the sensor-association form: Fit, Move (fit to a different
// sensor) and Remove. Fit/Move use a controlled searchable picker; the search
// text is not itself a sensor id and the orchestrator only enables submit after
// one of the returned available sensors is selected. Remove is a confirmation.
// No fetching or writes here; the orchestrator wires it.

import { esc, fitmentLabel } from '../../shared/tyre-format.js';
import { TYRE_APPLICATIONS } from '../../shared/tyre-vocab.js';
import { hasSensor } from '../../shared/tyre-status.js';

const MODES = {
  fit: { title: 'Fit', badge: 'Fit', tone: 'blue', sensorLabel: 'Sensor ID', submit: 'Fit tyre' },
  move: { title: 'Move', badge: 'Move', tone: 'amber', sensorLabel: 'New sensor ID', submit: 'Move tyre' },
  remove: { title: 'Remove', badge: 'Remove', tone: 'slate', submit: 'Remove tyre' }
};

function head(mode, tyre) {
  const m = MODES[mode];
  return `
    <div class="tm-form-head">
      <div class="tm-form-heading">
        <h2 class="tm-form-title">${esc(m.title)}</h2>
        <p class="tm-form-sub">Tyre <b>${esc(tyre.externalTyreId || '—')}</b></p>
      </div>
      <span class="tm-badge tm-tone-${m.tone}">${esc(m.badge)}</span>
    </div>`;
}

function applicationSelect(tyre) {
  const cur = tyre.tyreApplication || '';
  const opts = ['<option value="">Select…</option>']
    .concat(TYRE_APPLICATIONS.map((o) => `<option value="${esc(o)}"${o === cur ? ' selected' : ''}>${esc(o)}</option>`))
    .join('');
  return `
    <div class="tm-form-row">
      <label class="tm-form-label" for="tmaf-application">Application</label>
      <select id="tmaf-application" class="tm-input" data-field="application">${opts}</select>
    </div>`;
}

export function associationFormHTML(mode, tyre = {}) {
  const m = MODES[mode] || MODES.fit;

  if (mode === 'remove') {
    const fitted = hasSensor(tyre);
    return `
      <div class="tm-form">
        ${head(mode, tyre)}
        <div class="tm-form-msg" data-msg hidden></div>
        <div class="tm-form-grid">
          <div class="tm-form-row tm-form-row-wide">
            ${fitted
              ? `<p class="tm-assoc-lead">Remove tyre <b>${esc(tyre.externalTyreId || '')}</b> from its current position and return it to stock?</p>
                 <dl class="tm-kv"><div class="tm-kv-row"><dt>Currently fitted</dt><dd>${esc(fitmentLabel(tyre))}</dd></div></dl>
                 <p class="tm-muted tm-assoc-note">This is recorded on the tyre's history.</p>`
              : `<p class="tm-assoc-lead">This tyre is not currently fitted to a sensor.</p>`}
          </div>
        </div>
        <div class="tm-form-actions">
          <button type="button" class="tm-btn-secondary" data-cancel>Cancel</button>
          <button type="button" class="tm-btn-danger" data-submit ${fitted ? '' : 'disabled'}>${esc(m.submit)}</button>
        </div>
      </div>`;
  }

  // fit / move
  const fromRow = mode === 'move'
    ? `<div class="tm-form-row tm-form-row-wide">
         <dl class="tm-kv"><div class="tm-kv-row"><dt>Currently fitted</dt><dd>${esc(fitmentLabel(tyre))}</dd></div></dl>
       </div>`
    : '';

  return `
    <div class="tm-form">
      ${head(mode, tyre)}
      <div class="tm-form-msg" data-msg hidden></div>
      <div class="tm-form-grid">
        ${fromRow}
        <div class="tm-form-row tm-form-row-wide">
          <label class="tm-form-label" for="tmaf-sensorId">${esc(m.sensorLabel)}<span class="tm-req" aria-hidden="true">*</span></label>
          <div class="tm-sensor-picker" data-sensor-picker>
            <input id="tmaf-sensorId" type="search" class="tm-input" data-field="sensorSearch" autocomplete="off"
                   role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="tmaf-sensor-options"
                   placeholder="Search available sensors…" />
            <div id="tmaf-sensor-options" class="tm-sensor-options" data-sensor-options role="listbox" hidden></div>
            <p class="tm-sensor-hint" data-sensor-hint>Loading available sensors…</p>
          </div>
        </div>
        ${applicationSelect(tyre)}
      </div>

      <div class="tm-form-actions">
        <button type="button" class="tm-btn-secondary" data-cancel>Cancel</button>
        <button type="button" class="tm-btn-primary" data-submit>${esc(m.submit)}</button>
      </div>
    </div>`;
}
